<template>
  <div class="header-container">
    <router-link
      :to="campaignSpaceRoute"
    >
      <div class="text" v-text="space?.name || ''"></div>
    </router-link>
    <div class="item-separator"></div>
    <div class="text" v-text="campaign.campaign.internal_name" />
    <transition name="fade" mode="out-in">
      <Button v-if="!isPublicInvitationPending && !!publicInvitation" v-tooltip="$t('app.campaign.header.copyButton.tooltip')" class="icon round primary" @click="copyLink">
        <template #prefix>
          <LinkIcon style="width: 14px;"></LinkIcon>
        </template>
      </Button>
    </transition>
    <div class="right-side" v-if="buttons">
      <template v-for="(btn, index) in buttons" :key="index">
        <div v-if="btn.routeParams && !btn.disabled" class="button-wrapper">
          <router-link
            class="btn"
            :class="btn.class"
            :data-step="btn.step || null"
            :to="btn.routeParams"
          >
            <span v-text="btn.label" />
            <i v-if="btn.icon" :class="btn.icon" />
          </router-link>
        </div>
        <Button
          v-else
          :class="btn.class"
          :label="btn.label"
          :icon="btn.icon"
          :disabled="btn.disabled"
          @click="btn.click()"
        />
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";

a {
  text-decoration: none;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.header-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;

  .item-separator {
    &::after {
      content: "\f285";
      position: relative;

      font-family: bootstrap-icons;
      font-weight: 900;
      font-size: 17px;
      color: var(--secondary-color);
    }

    i {
      color: var(--secondary-color);
      height: 20px;
    }
  }


  .text {
    color: var(--secondary-color);
    font-family: $bioSans !important;
    font-size: 32px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
  }

  .right-side {
    margin-left: auto;
    display: flex;
    gap: 16px;
  }
}
</style>

<script setup lang="ts">
import { computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useToast } from "vue-toastification";

import { useCampaign } from "@/queries/campaigns.query";
import { useSpace } from "@/queries/space.query";

import LinkIcon from '@/assets/images/icon/campaign/link.svg?component';
import { useCampaignPublicInvitation } from "@/queries/invitation.query";

const route = useRoute();
const appStore = useAppStore();
const { t } = useI18n();
const toast = useToast();

const { data: space, isPending: isSpacePending } = useSpace();
const { data: campaign, isPending } = useCampaign(appStore.onboardingIsLive);
const { data: publicInvitation, isPending: isPublicInvitationPending } = useCampaignPublicInvitation();

const props = defineProps<{
  buttons?: any,
}>();

const campaignSpaceRoute = computed(() => {
  return {
    name: 'space',
    params: { spaceId: route.params.spaceId as string || "" },
  };
});

const campaignShareLink = computed(() => {
  if (isPending.value || !campaign.value || isPublicInvitationPending.value || !publicInvitation.value?.id)
    return null;

  return `${window.location.host.replace("app", "game")}/join?invitation_id=${
    publicInvitation.value.id
  }`;
});

async function copyLink() {
  try {
    await navigator.clipboard.writeText(campaignShareLink.value);
    toast.success(t('app.campaign.header.copyButton.successToast'));
  } catch (err) {
    toast.error(t('app.campaign.header.copyButton.errorToast'));
    console.error('Failed to copy:', err);
  }
}
</script>